import { useRef } from 'react'

import { Actions, Entities } from '@space-core/datahub'
import ImageSlider from '@space-shell/common/components/imageSlider'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useResizeObserver } from '@space-shell/common/hooks/useResizeObserver'
import type { Banners } from '@space-shell/common/services/membership'
import { BannerEmptyState } from '@space-shell/remotes/preview/components/bannerEmptyState'
import { useParentInfoStore } from '@space-shell/remotes/preview/store/parentInfo'

import clsx from 'clsx'
import styled from 'styled-components'

import { LoadingMessage } from '../imageSlider/loadingMessage'

type BannerSectionProps = {
  slideAuto: boolean
  slideTo?: number
  className?: string
  banners?: Banners[]
  isPreview?: boolean
  showBannerHighlight?: boolean
  showBannerControls?: boolean
}

const Section = styled.section`
  margin-top: calc(var(--spacer-12) * -1);
`

const BannerSection = ({
  banners,
  className,
  slideTo,
  slideAuto,
  isPreview = false,
  showBannerHighlight = false,
  showBannerControls = true
}: BannerSectionProps) => {
  const { sendDataHubEvent } = useDataHubEvents()
  const { isLoading } = useParentInfoStore()
  const ref = useRef<HTMLElement>(null)
  const { width } = useResizeObserver({ ref })

  if (!banners?.length) {
    if (isPreview) {
      return <BannerEmptyState />
    }

    return null
  }

  const handleClickBanner = () => {
    sendDataHubEvent({
      group: Entities.HOME_BANNER,
      name: Actions.CLICK_BANNER
    })
  }

  return (
    <Section
      ref={ref}
      data-test='banner-section'
      className={clsx(
        '_d-flex _flex-wrap _overflow-hidden _h-full _w-full _mb-6 _position-relative',
        className
      )}>
      {isLoading && <LoadingMessage />}
      <ImageSlider
        slides={banners}
        slideTo={slideTo}
        autoPlay={slideAuto}
        isPreview={isPreview}
        onClick={handleClickBanner}
        showBannerHighlight={showBannerHighlight}
        showBannerControls={showBannerControls}
        {...(isPreview && { parentPreviewWidth: width })}
      />
    </Section>
  )
}

export default BannerSection
